export type JobleEvent =
  | "landing_page_viewed"
  | "try_joble_clicked"
  | "book_demo_clicked"
  | "signin_clicked"
  | "talk_to_sales_clicked"
  | "lead_form_submitted"
  | "experience_started"
  | "business_information_submitted"
  | "sofia_interaction_started"
  | "first_interaction_completed"
  | "full_experience_completed"
  | "start_free_trial_clicked"
  | "signup_started"
  | "signup_completed";

type EventProps = Record<string, unknown>;
type AttributionMap = Record<string, string>;

const ATTRIBUTION_STORAGE_KEY = "joble_try_attribution";

const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_id",
  "gclid",
  "fbclid",
  "msclkid",
] as const;

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

/**
 * Read the stored acquisition attribution (UTM + ad click ids) for this session.
 */
export function getAttribution(): AttributionMap {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as AttributionMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Capture UTM / ad click parameters from the current URL and persist them for the
 * whole session, so the visitor's original source survives every in-page step.
 */
export function captureAttribution(): AttributionMap {
  if (typeof window === "undefined") return {};
  const merged: AttributionMap = { ...getAttribution() };
  try {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) {
        merged[key] = value;
        changed = true;
      }
    });
    if (changed) {
      window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(merged));
    }
  } catch {
    /* storage unavailable, ignore */
  }
  return merged;
}

/**
 * Fire a named funnel event. Pushes to window.dataLayer (GA/GTM compatible),
 * broadcasts a DOM event for other listeners, and logs in development.
 * Attribution + timestamp are attached automatically.
 */
export function track(event: JobleEvent, props: EventProps = {}): void {
  if (typeof window === "undefined") return;
  const payload: Record<string, unknown> = {
    event,
    ...getAttribution(),
    ...props,
    page: window.location.pathname,
    timestamp: new Date().toISOString(),
  };

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    /* ignore */
  }

  try {
    window.dispatchEvent(new CustomEvent("joble:track", { detail: payload }));
  } catch {
    /* ignore */
  }

  if (import.meta.env.DEV) {
    console.debug("[joble:track]", event, payload);
  }
}

/**
 * Append the visitor's stored attribution to an outbound URL so campaign data
 * carries through to the destination (e.g. the signup app or a scheduler).
 */
export function withAttribution(url: string): string {
  const attribution = getAttribution();
  const entries = Object.entries(attribution).filter(([, value]) => value);
  if (entries.length === 0) return url;
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://joble.app";
    const parsed = new URL(url, base);
    entries.forEach(([key, value]) => {
      if (!parsed.searchParams.has(key)) parsed.searchParams.set(key, value);
    });
    return parsed.toString();
  } catch {
    return url;
  }
}